// ============================================
// Statistics Tracker — replaces StatisticsTracker.cs
// Tracks gameplay counters, daily summaries,
// persisted in localStorage
// ============================================

import { events, Events } from '../engine/events.js';
import { variableManager } from '../engine/variables.js';

const STORAGE_KEY = 'PnO_Statistics';
const SAVE_INTERVAL = 30000; // 30 seconds

// Stat keys that can be incremented via #stats: tags
const VALID_STAT_TAGS = new Set([
    'blowjob', 'anal', 'hookup', 'strip', 'edge', 'glory_hole',
]);

class StatisticsTracker {
    constructor() {
        // Lifetime counters
        this._stats = {
            totalMoneyEarned: 0,
            totalMoneySpent: 0,
            totalChoicesMade: 0,
            itemsPurchased: 0,
            streamsCompleted: 0,
            daysSurvived: 0,
            rentPaymentsMade: 0,
            orgasms: 0,
            gloryHoleVisits: 0,
            blowjobsGiven: 0,
            analSex: 0,
            hookups: 0,
            timesStripped: 0,
            timesEdged: 0,
        };

        /** @type {Set<string>} all-time visited locations */
        this._visitedLocations = new Set();

        // Tracking state for delta detection
        this._lastKnownMoney = 0;
        this._lastKnownArousal = 0;
        this._isSyncing = false;

        // Daily tracking (in-memory, resets each day)
        this._daily = {
            moneyEarned: 0,
            moneySpent: 0,
            locations: new Set(),
            startStats: {},
        };

        this._dirty = false;
        this._saveTimer = null;

        // Load persisted data
        this._loadFromStorage();

        // Periodic save
        this._saveTimer = setInterval(() => this._flushIfDirty(), SAVE_INTERVAL);

        // Listen for events (scoped so destroy() tears them down)
        this._scope = events.createScope('statistics');
        this._scope.on(Events.STAT_CHANGED, (stat, value) => this._onStatChanged(stat, value));
        this._scope.on(Events.ITEM_PURCHASED, () => this._onItemPurchased());
        this._scope.on(Events.TAG_STATS, (tag) => this._onStatsTag(tag));
    }

    // ---- Public getters ----

    getStat(key) {
        switch (key) {
            case 'blowjob': return this._stats.blowjobsGiven;
            case 'anal': return this._stats.analSex;
            case 'hookup': return this._stats.hookups;
            case 'strip': return this._stats.timesStripped;
            case 'edge': return this._stats.timesEdged;
            case 'glory_hole': return this._stats.gloryHoleVisits;
            default: return this._stats[key] ?? 0;
        }
    }

    get totalMoneyEarned() { return this._stats.totalMoneyEarned; }
    get totalMoneySpent() { return this._stats.totalMoneySpent; }
    get totalChoicesMade() { return this._stats.totalChoicesMade; }
    get itemsPurchased() { return this._stats.itemsPurchased; }
    get streamsCompleted() { return this._stats.streamsCompleted; }
    get daysSurvived() { return this._stats.daysSurvived; }
    get rentPaymentsMade() { return this._stats.rentPaymentsMade; }
    get orgasms() { return this._stats.orgasms; }
    get blowjobsGiven() { return this._stats.blowjobsGiven; }
    get analSex() { return this._stats.analSex; }
    get hookups() { return this._stats.hookups; }
    get timesStripped() { return this._stats.timesStripped; }
    get timesEdged() { return this._stats.timesEdged; }
    get gloryHoleVisits() { return this._stats.gloryHoleVisits; }

    getLocationCount() {
        return this._visitedLocations.size;
    }

    getVisitedLocations() {
        return Array.from(this._visitedLocations);
    }

    /**
     * Get all stats as a plain object (for save system).
     */
    getSerializableState() {
        return {
            ...this._stats,
            visitedLocations: Array.from(this._visitedLocations),
        };
    }

    // ---- Recording ----

    recordChoiceMade() {
        this._stats.totalChoicesMade++;
        this._dirty = true;
    }

    recordStreamCompleted() {
        this._stats.streamsCompleted++;
        this._dirty = true;
    }

    recordRentPayment() {
        this._stats.rentPaymentsMade++;
        this._dirty = true;
    }

    recordLocationVisit(location) {
        if (!location) return;
        this._daily.locations.add(location);
        if (this._visitedLocations.add(location)) {
            // New location discovered
            this._dirty = true;
        }
    }

    // ---- Baselines (called on load) ----

    syncMoneyBaseline(money) {
        this._isSyncing = true;
        this._lastKnownMoney = money;
        this._isSyncing = false;
    }

    syncArousalBaseline(arousal) {
        this._isSyncing = true;
        this._lastKnownArousal = arousal;
        this._isSyncing = false;
    }

    // ---- Daily summary ----

    /**
     * Start tracking a new day. Call at the beginning of each day.
     * @param {object} [currentStats] - { Femininity, Submissiveness, Masculinity }
     */
    startNewDay(currentStats = {}) {
        this._daily.moneyEarned = 0;
        this._daily.moneySpent = 0;
        this._daily.locations.clear();
        this._daily.startStats = { ...currentStats };
    }

    /**
     * Get a formatted daily summary string (used by Ink external get_daily_summary).
     * @param {object} [currentStats] - current stat values for delta computation
     */
    getDailySummary(currentStats = {}) {
        const parts = [];

        // Money
        if (this._daily.moneyEarned > 0 && this._daily.moneySpent > 0) {
            parts.push(`Earned $${this._daily.moneyEarned}, spent $${this._daily.moneySpent}.`);
        } else if (this._daily.moneyEarned > 0) {
            parts.push(`Earned $${this._daily.moneyEarned}.`);
        } else if (this._daily.moneySpent > 0) {
            parts.push(`Spent $${this._daily.moneySpent}.`);
        }

        // Stat changes
        const statChanges = [];
        for (const stat of ['Femininity', 'Submissiveness', 'Masculinity']) {
            const baseline = this._daily.startStats[stat];
            const current = currentStats[stat];
            if (baseline !== undefined && current !== undefined) {
                const delta = current - baseline;
                if (delta > 0) statChanges.push(`${stat} +${delta}`);
                else if (delta < 0) statChanges.push(`${stat} ${delta}`);
            }
        }
        if (statChanges.length > 0) {
            parts.push(statChanges.join(', ') + '.');
        }

        // Locations (filter out Home/Global)
        const interesting = Array.from(this._daily.locations).filter(
            loc => loc !== 'Home' && loc !== 'Global'
        );
        if (interesting.length > 0) {
            parts.push('Visited: ' + interesting.join(', ') + '.');
        }

        // Reset for next day
        this.startNewDay(currentStats);

        if (parts.length === 0) return '';
        return 'Yesterday: ' + parts.join(' ');
    }

    // ---- Event handlers ----

    _onStatChanged(stat, value) {
        if (this._isSyncing) return;

        if (stat === 'Money') {
            const delta = value - this._lastKnownMoney;
            if (delta > 0) {
                this._stats.totalMoneyEarned += delta;
                this._daily.moneyEarned += delta;
            } else if (delta < 0) {
                this._stats.totalMoneySpent += -delta;
                this._daily.moneySpent += -delta;
            }
            this._lastKnownMoney = value;
            this._dirty = true;
        } else if (stat === 'Day') {
            if (value > this._stats.daysSurvived) {
                this._stats.daysSurvived = value;
                this._dirty = true;
            }
        } else if (stat === 'Arousal') {
            // Orgasm detection: arousal drops from >=90 to <20
            if (this._lastKnownArousal >= 90 && value < 20) {
                this._stats.orgasms++;
                this._dirty = true;
            }
            this._lastKnownArousal = value;
        }
    }

    _onItemPurchased() {
        this._stats.itemsPurchased++;
        this._dirty = true;
    }

    _onStatsTag(tag) {
        const key = tag.toLowerCase().trim();
        if (!VALID_STAT_TAGS.has(key)) {
            console.warn(`[Statistics] Unknown stats tag: '${tag}'`);
            return;
        }

        switch (key) {
            case 'blowjob': this._stats.blowjobsGiven++; break;
            case 'anal': this._stats.analSex++; break;
            case 'hookup': this._stats.hookups++; break;
            case 'strip': this._stats.timesStripped++; break;
            case 'edge': this._stats.timesEdged++; break;
            case 'glory_hole': this._stats.gloryHoleVisits++; break;
        }
        this._dirty = true;
    }

    // ---- Persistence ----

    _loadFromStorage() {
        try {
            const json = localStorage.getItem(STORAGE_KEY);
            if (!json) return;
            const data = JSON.parse(json);

            // Restore counters
            for (const key of Object.keys(this._stats)) {
                if (typeof data[key] === 'number') {
                    this._stats[key] = data[key];
                }
            }

            // Restore locations
            if (Array.isArray(data.visitedLocations)) {
                for (const loc of data.visitedLocations) {
                    this._visitedLocations.add(loc);
                }
            }
        } catch (e) {
            console.warn('[Statistics] Failed to load from storage:', e);
        }
    }

    _saveToStorage() {
        try {
            const data = this.getSerializableState();
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch (e) {
            console.warn('[Statistics] Failed to save:', e);
        }
    }

    _flushIfDirty() {
        if (this._dirty) {
            this._saveToStorage();
            this._dirty = false;
        }
    }

    /**
     * Restore from save data.
     */
    restoreFromSave(data) {
        if (!data || typeof data !== 'object') return;

        for (const key of Object.keys(this._stats)) {
            if (typeof data[key] === 'number') {
                this._stats[key] = data[key];
            }
        }
        if (Array.isArray(data.visitedLocations)) {
            this._visitedLocations.clear();
            for (const loc of data.visitedLocations) {
                this._visitedLocations.add(loc);
            }
        }
        // Re-seed the delta baselines to the just-loaded Money/Arousal so the
        // next STAT_CHANGED produces a correct delta. Without this, a baseline
        // left over from a prior in-session game (or the constructor's 0) makes
        // the first post-load money change count a spurious earn/spend, and the
        // arousal orgasm-detection misfires. importState (loader step 2) runs
        // before this (step 6), so variableManager already holds the loaded values.
        try {
            this._lastKnownMoney = variableManager.getTotalValue('Money') ?? 0;
            this._lastKnownArousal = variableManager.getTotalValue('Arousal') ?? 0;
        } catch (e) {
            this._lastKnownMoney = 0;
            this._lastKnownArousal = 0;
        }
        this._dirty = true;
        this._flushIfDirty();
    }

    /**
     * Reset all statistics (new game).
     */
    resetAll() {
        for (const key of Object.keys(this._stats)) {
            this._stats[key] = 0;
        }
        this._visitedLocations.clear();
        this._lastKnownMoney = 0;
        this._lastKnownArousal = 0;
        this._daily.moneyEarned = 0;
        this._daily.moneySpent = 0;
        this._daily.locations.clear();
        this._daily.startStats = {};

        localStorage.removeItem(STORAGE_KEY);
    }

    destroy() {
        if (this._saveTimer) {
            clearInterval(this._saveTimer);
            this._saveTimer = null;
        }
        if (this._scope) {
            this._scope.destroy();
            this._scope = null;
        }
        this._flushIfDirty();
    }
}

export const statisticsTracker = new StatisticsTracker();
